import React, { useState } from 'react';
import { Select, MenuItem, FormControl, InputLabel } from '@material-ui/core';

export enum DateShortcut {
    Custom = 'custom',
    Today = 'today',
    ThisMonth = 'this_month',
    ThisQuarter = 'this_quarter',
    ThisYear = 'this_year',
    Yesterday = 'yesterday',
    LastMonth = 'last_month',
    LastQuarter = 'last_quarter',
    LastYear = 'last_year',
    LastYearToday = 'last_year_today',
    LastYearThisMonth = 'last_year_this_month',
    LastYearThisQuarter = 'last_year_this_quarter',
}

// Helper functions for date calculations
const getStartOfMonth = (date: Date): Date => new Date(date.getFullYear(), date.getMonth(), 1);
const getEndOfMonth = (date: Date): Date => new Date(date.getFullYear(), date.getMonth() + 1, 0);
const getStartOfQuarter = (date: Date): Date => {
    const quarter = Math.floor(date.getMonth() / 3);
    return new Date(date.getFullYear(), quarter * 3, 1);
};
const getEndOfQuarter = (date: Date): Date => {
    const quarter = Math.floor(date.getMonth() / 3);
    return new Date(date.getFullYear(), quarter * 3 + 3, 0);
};
const getStartOfYear = (date: Date): Date => new Date(date.getFullYear(), 0, 1);
const getEndOfYear = (date: Date): Date => new Date(date.getFullYear(), 11, 31);

// Additional helper functions for "Last Year" comparisons
const getStartOfDay = (date: Date): Date => new Date(date.getFullYear(), date.getMonth(), date.getDate());
const getEndOfDay = (date: Date): Date => new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);

const getLastYearSameDay = (ref: Date): Date => new Date(ref.getFullYear() - 1, ref.getMonth(), ref.getDate());
const getLastYearSameMonthStart = (ref: Date): Date => new Date(ref.getFullYear() - 1, ref.getMonth(), 1);
const getLastYearSameMonthEnd = (ref: Date): Date => new Date(ref.getFullYear() - 1, ref.getMonth() + 1, 0, 23, 59, 59, 999);
const getLastYearSameQuarterStart = (ref: Date): Date => {
    const quarter = Math.floor(ref.getMonth() / 3);
    return new Date(ref.getFullYear() - 1, quarter * 3, 1);
};
const getLastYearSameQuarterEnd = (ref: Date): Date => {
    const quarter = Math.floor(ref.getMonth() / 3);
    return new Date(ref.getFullYear() - 1, quarter * 3 + 3, 0, 23, 59, 59, 999);
};

interface ShortcutOption {
    value: string;
    label: string;
    getRange?: () => { startDate: Date; endDate: Date }; // Optional getRange function for custom shortcuts
}

interface DateShortcutDropdownProps {
    label: string;
    setStartDate: (date: Date | null) => void;
    setEndDate: (date: Date | null) => void;
    className?: string;
    value: string; // Controlled component: value passed from parent
    onChange: (value: string) => void; // Controlled component: callback to parent
    shortcutOptions: ShortcutOption[]; // All shortcut options (default and custom)
}

export const DEFAULT_SHORTCUT_OPTIONS = [
    
    { value: DateShortcut.Today, label: 'Today' },
    { value: DateShortcut.ThisMonth, label: 'This Month' },
    { value: DateShortcut.ThisQuarter, label: 'This Quarter' },
    { value: DateShortcut.ThisYear, label: 'This Year' },
    { value: DateShortcut.Yesterday, label: 'Yesterday' },
    { value: DateShortcut.LastMonth, label: 'Last Month' },
    { value: DateShortcut.LastQuarter, label: 'Last Quarter' },
    { value: DateShortcut.LastYear, label: 'Last Year' },
    { value: DateShortcut.LastYearToday, label: 'Last Year Today' },
    { value: DateShortcut.LastYearThisMonth, label: 'Last Year This Month' },
    { value: DateShortcut.LastYearThisQuarter, label: 'Last Year This Quarter' },
    { value: DateShortcut.Custom, label: 'Custom Range' },
];

const DateShortcutDropdown: React.FC<DateShortcutDropdownProps> = ({ label, setStartDate, setEndDate, className, value, onChange, shortcutOptions }) => {

    React.useEffect(() => {
        const shortcutToProcess = value === 'custom_range' ? DateShortcut.Custom : value;
        
        const selectedOption = shortcutOptions.find(option => option.value === shortcutToProcess);

        if (selectedOption && selectedOption.getRange) {
            const { startDate, endDate } = selectedOption.getRange();
            setStartDate(startDate);
            setEndDate(endDate);
            return;
        }
        
        const todayDate = new Date();
        let newStartDate: Date | null = null;
        let newEndDate: Date | null = null;

        switch (shortcutToProcess) {
            case DateShortcut.Today:
                newStartDate = getStartOfDay(todayDate);
                newEndDate = getEndOfDay(todayDate);
                break;
            case DateShortcut.ThisMonth:
                newStartDate = getStartOfMonth(todayDate);
                newEndDate = getEndOfMonth(todayDate);
                break;
            case DateShortcut.ThisQuarter:
                newStartDate = getStartOfQuarter(todayDate);
                newEndDate = getEndOfQuarter(todayDate);
                break;
            case DateShortcut.ThisYear:
                newStartDate = getStartOfYear(todayDate);
                newEndDate = getEndOfYear(todayDate);
                break;
            case DateShortcut.Yesterday: {
                const yesterdayDt = new Date(todayDate);
                yesterdayDt.setDate(todayDate.getDate() - 1);
                newStartDate = getStartOfDay(yesterdayDt);
                newEndDate = getEndOfDay(yesterdayDt);
                break;
            }
            case DateShortcut.LastMonth: {
                const lastMonthDt = new Date(todayDate.getFullYear(), todayDate.getMonth() - 1, 1);
                newStartDate = getStartOfMonth(lastMonthDt);
                newEndDate = getEndOfMonth(lastMonthDt);
                break;
            }
            case DateShortcut.LastQuarter: {
                const currentQuarter = Math.floor(todayDate.getMonth() / 3);
                const firstMonthOfCurrentQuarter = currentQuarter * 3;
                const firstDayOfLastQuarter = new Date(todayDate.getFullYear(), firstMonthOfCurrentQuarter - 3, 1);
                newStartDate = getStartOfQuarter(firstDayOfLastQuarter);
                newEndDate = getEndOfQuarter(firstDayOfLastQuarter);
                break;
            }
            case DateShortcut.LastYear: {
                const lastYearDt = new Date(todayDate.getFullYear() - 1, 0, 1);
                newStartDate = getStartOfYear(lastYearDt);
                newEndDate = getEndOfYear(lastYearDt);
                break;
            }
            case DateShortcut.LastYearToday: {
                const lastYearTodayDate = getLastYearSameDay(todayDate);
                newStartDate = getStartOfDay(lastYearTodayDate);
                newEndDate = getEndOfDay(lastYearTodayDate);
                break;
            }
            case DateShortcut.LastYearThisMonth: {
                newStartDate = getLastYearSameMonthStart(todayDate); // Uses current month of today, but last year
                newEndDate = getLastYearSameMonthEnd(todayDate);   // Uses current month of today, but last year
                break;
            }
            case DateShortcut.LastYearThisQuarter: {
                newStartDate = getLastYearSameQuarterStart(todayDate); // Uses current quarter of today, but last year
                newEndDate = getLastYearSameQuarterEnd(todayDate);   // Uses current quarter of today, but last year
                break;
            }
            case DateShortcut.Custom: // Explicitly handle 'custom' to not change dates
                // For 'custom' or if value is 'custom_range' (mapped to 'custom'),
                // or any other unhandled value, do not automatically change dates.
                // The user will use the date pickers directly.
                return; 
            default:
                // If a shortcut is provided that does not have a getRange function and is not a known default shortcut, throw an error
                throw new Error(`Unknown date shortcut: ${shortcutToProcess}. Please provide a 'getRange' function for custom shortcuts.`);
        }
        setStartDate(newStartDate);
        setEndDate(newEndDate);
    }, [value, setStartDate, setEndDate, shortcutOptions]);

    const handleChange = (event: React.ChangeEvent<{ value: unknown }>) => {
        const shortcutType = event.target.value as string;
        onChange(shortcutType); // Notify parent about the change
    };
    
    // Determine the value to display in the Select component.
    // If parent passes 'custom_range', display it as 'custom' in this dropdown.
    const displayValue = value === 'custom_range' ? DateShortcut.Custom : value;

    return (
        <FormControl fullWidth className={className}>
            <InputLabel>{label}</InputLabel>
            <Select value={displayValue} onChange={handleChange}>
                {shortcutOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                        {option.label}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
};

export default DateShortcutDropdown;
